import { useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { useIsPortrait } from '../../../util/breakpoints'
import { isInScrollRange } from '../../../util/scrollPercentage'
import { AnimationItem } from 'lottie-web'
import '../../../lib/lottie_light.min.js'
import { getFirstFrameUrl, getLastFrameUrl } from '../../../util/path'

export const LottieContainer = styled.div`
  position: relative;
`

export const LottieDiv = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100vh;
`

export const Poster = styled.img`
  position: absolute;
  top: 0px;
  left: 0px;
  height: 100vh;
  z-index: 2;
  width: 100%;
  object-fit: cover;

  transition: opacity 0.3s ease-in-out;
  opacity: 0;
  &.show {
    opacity: 1;
  }
`

const getCurrentPosterUrl = (lottieUrl: string, props: LottieProps) => {
  if (props.playback === 'scroll' && props.scrollPercentage > 0.5) {
    return getLastFrameUrl(lottieUrl)
  }
  return getFirstFrameUrl(lottieUrl)
}

export interface LottieProps {
  playback: 'scroll' | 'loop' | 'play_once'
  lottieLandscape: string
  lottiePortrait: string
  scrollPercentage: number
  shouldLoadAnimation: boolean
}

export function Lottie(props: LottieProps) {
  const isPortrait = useIsPortrait()
  const [animationJsonData, setAnimationJsonData] =
    useState<Record<string, unknown>>()
  const [animationContainsImage, setAnimationContainsImage] = useState(false)

  const [animation, setAnimation] = useState<AnimationItem>()
  const [animationLoaded, setAnimationLoaded] = useState(false)
  const [animationPlaying, setAnimationPlaying] = useState(false)
  const lottieDivRef = useRef<HTMLDivElement>(null)

  const lottieUrl =
    isPortrait && props.lottiePortrait
      ? props.lottiePortrait
      : props.lottieLandscape

  // fetch & process lottie animation Data
  useEffect(() => {
    if (props.shouldLoadAnimation && lottieUrl) {
      fetch(lottieUrl)
        .then((response) => response.json())
        .then((data) => {
          const containsImages = data.assets?.length > 0

          if (data.assets) {
            // get path to lottie.json and use to adjust relative image paths
            // Reason: asset.p will be like 'images/', which will not work when
            // we provide the animation as a whole json. We need to adjust asset.p
            // to https://lottieurl.com/lottie/images
            const pathToLottieJson = lottieUrl.slice(0, lottieUrl.lastIndexOf('/'))

            data.assets = data.assets.map((asset: { u: string }): { u: string } => {
              if (asset.u) {
                asset.u = `${pathToLottieJson}/${asset.u}`
              }
              return asset
            })
          }

          setAnimationContainsImage(containsImages)
          setAnimationJsonData(data)
        })
        .catch((error) => {
          console.error(`Could not fetch lottie '${lottieUrl}'`, error)
        })
    }
  }, [lottieUrl, props.shouldLoadAnimation])

  useEffect(() => {
    if (props.shouldLoadAnimation) {
      if (!animation && lottieDivRef.current && animationJsonData) {
        const newAnimation = window.lottie.loadAnimation({
          animationData: animationJsonData,
          container: lottieDivRef.current,
          renderer: 'svg',
          rendererSettings: {
            preserveAspectRatio: 'xMidYMid slice',
          },
          loop: props.playback === 'loop',
          autoplay: false,
        })

        // Reason: If animation contains images, we will wait for 'loaded_images' event
        // before removing the poster to avoid flickering
        if (animationContainsImage) {
          newAnimation.addEventListener('loaded_images', () => {
            setAnimationLoaded(true)
          })
        } else {
          setAnimationLoaded(true)
        }

        setAnimation(newAnimation)
      }
    } else {
      if (animation) {
        setAnimationPlaying(false)
        setAnimationLoaded(false)
        setAnimation(undefined)
        animation?.destroy()
      }
    }
  }, [
    animation,
    animationContainsImage,
    animationJsonData,
    props.playback,
    props.shouldLoadAnimation,
  ])

  // Update animation if loop | play_once
  useEffect(() => {
    if (
      animation &&
      (props.playback === 'loop' || props.playback === 'play_once')
    ) {
      if (isInScrollRange(props.scrollPercentage) && !animationPlaying) {
        animation.goToAndPlay(0, true)
        setAnimationPlaying(true)
      }
    }
  }, [
    animation,
    animationPlaying,
    props.playback,
    props.scrollPercentage,
  ])

  // Update animation if scroll
  useEffect(() => {
    if (animation && animationLoaded && props.playback === 'scroll') {
      let currentFrame = 0
      if (animation.totalFrames > 0) {
        // Reachable frames go from 0 to totalFrames - 1
        currentFrame = (animation.totalFrames - 1) * props.scrollPercentage
      }

      animation.goToAndStop(currentFrame, true)
      setAnimationPlaying(true)
    }
  }, [animation, animationLoaded, props.playback, props.scrollPercentage])

  return (
    <LottieContainer>
      <Poster
        className={!animationPlaying ? 'show' : ''}
        src={getCurrentPosterUrl(lottieUrl, props)}
        alt={''}
      />
      <LottieDiv ref={lottieDivRef} />
    </LottieContainer>
  )
}
